/**
 * Laying several objects out at once: a row, a grid or a circle, spaced by
 * their drawn bounds. One call and one undo step for what would otherwise be
 * a measurement per object and a computed position each.
 */

import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';

import type { SceneObject } from '../../core/SceneObject';
import { setTransforms, type TransformEntry } from '../authoring/transformOps';
import { rollBack } from '../authoring/steps';
import { defineTool, drawnBounds, fail, keep, refusalText, resolveEntry, whereText, worldToLocal, type Vec3 } from './input';
import type { SceneTool } from './types';

const LAYOUTS = ['row', 'grid', 'circle'] as const;
type Layout = typeof LAYOUTS[number];
const AXES = { x: 0, y: 1, z: 2 } as const;

/** The parent-local position that moves `object` by `shift` in world space. */
function shiftedLocal(object: SceneObject, shift: Vector3): Vec3 {
    return worldToLocal(object.transform.getAbsolutePosition().add(shift), object.transform.parent as TransformNode | null);
}

interface Box { object: SceneObject; min: number[]; max: number[] }

const centre = (box: Box, axis: number): number => (box.min[axis] + box.max[axis]) / 2;
const extent = (box: Box, axis: number): number => box.max[axis] - box.min[axis];

/** The world shift for each object, the first staying where it is. */
function plan(boxes: Box[], layout: Layout, axis: number, gap: number, columns: number, radius: number | undefined, spacing: number | undefined): number[][] {
    const first = boxes[0];
    if (layout === 'row' && spacing !== undefined) {
        // Centre to centre, whatever their sizes.
        return boxes.map((box, i) => [0, 1, 2].map(a => a === axis ? centre(first, a) + i * spacing - centre(box, a) : a === 1 ? 0 : centre(first, a) - centre(box, a)));
    }
    if (layout === 'row') {
        // Along the axis, each object's near side `gap` past the previous one's far side; centred on the first across it.
        let cursor = first.max[axis] + gap;
        return boxes.map((box, i) => {
            const shift = [0, 1, 2].map(a => a === axis || a === 1 ? 0 : centre(first, a) - centre(box, a));
            if (i > 0) {
                shift[axis] = cursor - box.min[axis];
                cursor += extent(box, axis) + gap;
            }
            return shift;
        });
    }
    if (layout === 'grid') {
        // Columns along x, rows along +z (toward the viewer), each cell as big as the largest footprint plus the gap.
        const cellX = spacing ?? Math.max(...boxes.map(b => extent(b, 0))) + gap;
        const cellZ = spacing ?? Math.max(...boxes.map(b => extent(b, 2))) + gap;
        return boxes.map((box, i) => {
            const [col, row] = [i % columns, Math.floor(i / columns)];
            return [centre(first, 0) + col * cellX - centre(box, 0), 0, centre(first, 2) + row * cellZ - centre(box, 2)];
        });
    }
    // A circle in the ground plane through the first object, which stays put at angle 0 (+x of the centre).
    const widest = Math.max(...boxes.map(b => Math.max(extent(b, 0), extent(b, 2))));
    const r = radius ?? Math.max(widest, (boxes.length * (widest + gap)) / (2 * Math.PI));
    const [cx, cz] = [centre(first, 0) - r, centre(first, 2)];
    return boxes.map((box, i) => {
        const angle = (2 * Math.PI * i) / boxes.length;
        return [cx + r * Math.cos(angle) - centre(box, 0), 0, cz - r * Math.sin(angle) - centre(box, 2)];
    });
}

const arrangeTool = defineTool({
    name: 'arrange_objects',
    verb: 'Arranging objects',
    edits: true,
    inputSchema: {
        type: 'object',
        properties: {
            objects: { type: 'array', items: { type: 'string' } },
            layout: { type: 'string', enum: [...LAYOUTS] },
            axis: { type: 'string', enum: Object.keys(AXES) },
            gap: { type: 'number' },
            spacing: { type: 'number' },
            columns: { type: 'number' },
            radius: { type: 'number' },
        },
        required: ['objects'],
    },
    async run(input, host) {
        const refs = Array.isArray(input.objects) ? input.objects : [];
        if (refs.length < 2) return fail('Error: arrange_objects needs at least two `objects`.');
        const layout: Layout = input.layout === undefined ? 'row' : input.layout as Layout;
        if (!LAYOUTS.includes(layout)) return fail(`Error: layout must be one of ${LAYOUTS.join(', ')}.`);
        const axisName = input.axis === undefined ? 'x' : typeof input.axis === 'string' ? input.axis : '';
        if (!Object.hasOwn(AXES, axisName)) return fail('Error: axis must be x, y or z.');
        const axis = AXES[axisName as keyof typeof AXES];
        const gap = input.gap === undefined ? 0 : input.gap;
        if (typeof gap !== 'number' || !Number.isFinite(gap)) return fail('Error: gap is a number of metres.');
        const columns = input.columns === undefined ? Math.ceil(Math.sqrt(refs.length)) : input.columns;
        if (typeof columns !== 'number' || !Number.isInteger(columns) || columns < 1) return fail('Error: columns is a whole number, 1 or more.');
        if (input.radius !== undefined && (typeof input.radius !== 'number' || !(input.radius > 0))) return fail('Error: radius is a number of metres above 0.');
        if (input.spacing !== undefined && (typeof input.spacing !== 'number' || !(input.spacing > 0))) return fail('Error: spacing is a centre-to-centre distance in metres above 0.');
        if (input.spacing !== undefined && input.gap !== undefined) return fail('Error: give gap (between sides) or spacing (centre to centre), not both.');
        if (input.spacing !== undefined && layout === 'circle') return fail('Error: spacing is for a row or grid; a circle takes radius or gap.');
        if (input.axis !== undefined && layout !== 'row') return fail('Error: axis is for a row; a grid runs along x and z, a circle lies on the ground.');
        if (input.columns !== undefined && layout !== 'grid') return fail('Error: columns is for a grid.');
        if (input.radius !== undefined && layout !== 'circle') return fail('Error: radius is for a circle.');
        if (input.radius !== undefined && input.gap !== undefined) return fail('Error: a circle takes radius or gap, not both.');

        const boxes: Box[] = [];
        for (const ref of refs) {
            const object = resolveEntry(host, ref);
            if (typeof object === 'string') return fail(`Error: "${String(ref)}": ${object}. Nothing moved.`);
            if (boxes.some(b => b.object === object)) return fail(`Error: "${object.name}" is listed twice. Nothing moved.`);
            const bounds = drawnBounds(object);
            if (!bounds) return fail(`Error: "${object.name}" draws nothing, so it has no size to space by. Nothing moved.`);
            boxes.push({ object, min: bounds.min.asArray(), max: bounds.max.asArray() });
        }
        // Moving an object moves what is under it, so one listed with its ancestor would move twice.
        for (const { object } of boxes) {
            for (let up = object.getParent(); up; up = up.getParent()) {
                const ancestor = boxes.find(b => b.object === up);
                if (ancestor) return fail(`Error: "${object.name}" is inside "${ancestor.object.name}", which is also listed; arrange one or the other. Nothing moved.`);
            }
        }
        // A row along y stacks them: each rests on the one before.
        const shifts = plan(boxes, layout, axis, gap, columns, input.radius, input.spacing);
        const entries: TransformEntry[] = boxes.map((box, i) => ({ object: box.object, position: shiftedLocal(box.object, new Vector3(...shifts[i])) }));
        const result = await setTransforms(entries, host.ports());
        const [refused] = result.refused;
        if (refused) {
            // The others moved: take them back, so a refusal leaves the layout as it was.
            if (result.command) await rollBack([result.command], null);
            return fail(`Error: ${refusalText(host, refused)} Nothing moved.`);
        }
        keep(host, 'arrange_objects', result);
        const how = layout === 'row' ? `in a row along ${axisName}` : layout === 'grid' ? `in a grid of ${columns} columns` : 'in a circle';
        const spaced = typeof input.spacing === 'number' ? `, ${input.spacing} m centre to centre`
            : layout === 'circle' ? (typeof input.radius === 'number' ? `, radius ${input.radius} m` : gap ? ` with ${gap} m gaps` : '')
            : gap ? ` with ${gap} m gaps` : '';
        return { text: [`Arranged ${boxes.length} objects ${how}${spaced}.`, ...boxes.map(b => `  "${b.object.name}":${whereText(b.object)}`)].join('\n') };
    },
});

export const ARRANGE_TOOLS: readonly SceneTool[] = [arrangeTool];
